<script lang="ts">
import { PartnerModule } from "@/store/modules/partner";
import { Component, Vue } from "vue-property-decorator";
import { UserModule } from "@/store/modules/me-user.store";
import { ConfigModule } from "@/store/modules/config";

@Component({})
export default class PartnerFallbackMixin extends Vue {
  isPartnerFallbackLoading = false;

  /**
   * Whether the partner of the route is already the active one, so {@link trySetByRouteOrDefault}
   * neither requests anything nor switches the partner.
   *
   * Views use it to decide before their first render whether to show a loading state: toggling
   * it on and off again when nothing has to be loaded unmounts and remounts the content, which
   * makes tables fetch their first page twice.
   */
  isPartnerResolvedForRoute(): boolean {
    const partnerId = this.$route.params.partnerId;
    if (!partnerId || !PartnerModule.partnersMap.has(partnerId)) {
      return false;
    }

    const partner = PartnerModule.partner;

    return (partner?._id || partner?.id) === partnerId;
  }

  /**
   * Try to set a given partner by $router (to be able to overwrite) or set the partner by store.
   * If no partner is set via router or store, try to fetch all partners and set the first as active.
   * If no active partner is found redirect to an error page.
   */
  async trySetByRouteOrDefault(): Promise<boolean> {
    // Fetch all partners if not in store.
    if (PartnerModule.partners.length === 0) {
      try {
        this.isPartnerFallbackLoading = true;
        await PartnerModule.getPartners();
      } catch (error) {
        this.$log.error(error);
        this.$toast.error("Fehler beim Laden der Partner.");
        this.$router.push({ name: "PartnerError" });
        return false;
      } finally {
        this.isPartnerFallbackLoading = false;
      }
    }

    const partnerId = this.$route.params.partnerId;

    // Set partner by param id
    if (partnerId) {
      const p = PartnerModule.partnersMap.get(partnerId);

      if (p) {
        PartnerModule.setPartner(p);
        this.$log.info("Partner set by param: partnerId");

        return true;
      }

      try {
        this.isPartnerFallbackLoading = true;

        await PartnerModule.getPartnerById(partnerId);
        this.$log.info("Partner set by param: partnerId");
      } catch (error) {
        this.$log.error(error);
        this.$log.warn("Partner could not set by param partnerId");
        this.$toast.error("Fehler beim Laden der Partner.");
        this.$router.push({ name: "PartnerError" });

        return false;
      } finally {
        this.isPartnerFallbackLoading = false;
      }
    }

    if (PartnerModule.partners.length > 0 && PartnerModule.partners[0]) {
      this.$log.info("Not Partner context set. Try set first partner");

      /**
       * Set the first partner.
       * On admin select a predefined if found.
       * Non admin get the first in the list.
       */
      let activePartner = PartnerModule.partners[0];
      if (UserModule.isAdmin) {
        activePartner =
          PartnerModule.partners.find((p) => p._id === ConfigModule.preferredAdminThgPartner) ||
          PartnerModule.partners[0];
      }
      PartnerModule.setPartner(activePartner);

      return true;
    }

    this.$router.push({ name: "PartnerError" });

    return false;
  }
}
</script>
